import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  List,
  ListItem,
  FormControlLabel,
  Checkbox,
  Collapse,
  IconButton,
  LinearProgress,
  Button,
} from "@mui/material";
import { ExpandLess, ExpandMore, NavigateBefore } from "@mui/icons-material";
import apiService from "../../../service/Api";

// Types
interface ListItemType {
  id: number;
  name: string;
  description?: string | null;
  points: number;
}

interface SectionType {
  id: number;
  name: string;
  list_items: ListItemType[];
}

interface ChecklistResponse {
  id: number;
  name: string;
  description?: string | null;
  roles_name?: string | null;
  phase?: string | null;
  updated_at: string;
  sections: SectionType[];
}

const ViewChecklistItem: React.FC = () => {
  const { checklistId, sectionId } = useParams<{
    checklistId: string;
    sectionId: string;
  }>();
  const navigate = useNavigate();

  const [section, setSection] = useState<SectionType | null>(null);
  const [checklist, setChecklist] = useState<ChecklistResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [openSections, setOpenSections] = useState<Record<number, boolean>>({});
  const [checkedItems, setCheckedItems] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (!checklistId || !sectionId) return;

    const fetchChecklist = async () => {
      setLoading(true);
      try {
        const res = await apiService.get<ChecklistResponse>(
          `/api/checklists/checklist/${checklistId}/`
        );
        setChecklist(res.data);

        const sectionsState: Record<number, boolean> = {};
        res.data.sections.forEach((s) => {
          sectionsState[s.id] = true;
        });
        setOpenSections(sectionsState);

        const itemsState: Record<number, boolean> = {};
        res.data.sections.forEach((s) =>
          s.list_items.forEach((i) => {
            itemsState[i.id] = false;
          })
        );
        setCheckedItems(itemsState);

        const found = res.data.sections.find((s) => s.id === Number(sectionId));
        setSection(found ?? null);
      } catch (err) {
        console.error("Failed to fetch checklist:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchChecklist();
  }, [checklistId, sectionId]);

  const toggleSection = (id: number) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleItem = (id: number) => {
    setCheckedItems((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading)
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );

  if (!section || !checklist)
    return (
      <Box mt={6} textAlign="center">
        <Typography variant="h6">Section not found</Typography>
        <Button
          variant="outlined"
          startIcon={<NavigateBefore />}
          sx={{ mt: 2 }}
          onClick={() => navigate(`/crew-management/checklists/${checklistId}`)}
        >
          Back to Checklist
        </Button>
      </Box>
    );

  const totalPoints = section.list_items.reduce(
    (sum, item) => sum + item.points,
    0
  );
  const earnedPoints = section.list_items.reduce(
    (sum, item) => sum + (checkedItems[item.id] ? item.points : 0),
    0
  );
  const progressPercentage =
    totalPoints > 0 ? (earnedPoints / totalPoints) * 100 : 0;

  return (
    <Box p={4}>
      {/* Checklist Info */}
      <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Typography variant="h4" gutterBottom>
          {checklist.name}
        </Typography>
        <Typography variant="subtitle1" gutterBottom>
          Role: {checklist.roles_name || "Unknown"} | Phase:{" "}
          {checklist.phase || "N/A"}
        </Typography>
        <Typography variant="subtitle2" gutterBottom>
          Last Updated: {new Date(checklist.updated_at).toLocaleString()}
        </Typography>
        {checklist.description && (
          <Typography variant="body1">{checklist.description}</Typography>
        )}
      </Paper>

      {/* Section Header */}
      <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          onClick={() => toggleSection(section.id)}
          sx={{ cursor: "pointer" }}
        >
          <Typography variant="h6">{section.name}</Typography>
          <IconButton size="small">
            {openSections[section.id] ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        {/* Points & Progress */}
        <Box sx={{ mt: 1, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Points: {earnedPoints} / {totalPoints}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progressPercentage}
            sx={{
              height: 12,
              borderRadius: 6,
              backgroundColor: "#f0f0f0",
              "& .MuiLinearProgress-bar": {
                borderRadius: 6,
                backgroundColor:
                  progressPercentage === 100
                    ? "#4BA663"
                    : progressPercentage > 50
                    ? "#FFA726"
                    : "#F44336",
              },
            }}
          />
        </Box>

        {/* List Items */}
        <Collapse in={openSections[section.id]} timeout="auto" unmountOnExit>
          <List>
            {section.list_items.map((item) => (
              <ListItem
                key={item.id}
                sx={{
                  pl: 0,
                  py: 1,
                  borderBottom: "1px solid #eee",
                  "&:hover": { bgcolor: "#f9f9f9" },
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box display="flex" alignItems="center">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checkedItems[item.id] || false}
                        onChange={() => toggleItem(item.id)}
                        color="primary"
                      />
                    }
                    label={item.name}
                  />
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {item.points} pts
                </Typography>
              </ListItem>
            ))}
          </List>
        </Collapse>
      </Paper>
    </Box>
  );
};

export default ViewChecklistItem;
